import { useCallback, useEffect, useState } from "react";

export type LayoutPreference = "auto" | "mobile" | "desktop";
export type LayoutMode = "mobile" | "desktop";

const STORAGE_KEY = "fcr-layout";
const MOBILE_MAX_WIDTH = 767;

function readPreference(): LayoutPreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "mobile" || v === "desktop" || v === "auto") return v;
  } catch {
    /* ignore */
  }
  return "auto";
}

function detectAutoMode(): LayoutMode {
  if (typeof window === "undefined") return "desktop";
  const narrow = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  return narrow && coarse ? "mobile" : "desktop";
}

function resolveMode(preference: LayoutPreference): LayoutMode {
  if (preference === "mobile") return "mobile";
  if (preference === "desktop") return "desktop";
  return detectAutoMode();
}

export function setLayoutPreference(preference: LayoutPreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* ignore */
  }
  window.location.reload();
}

export function useLayoutMode(): {
  mode: LayoutMode;
  preference: LayoutPreference;
  setPreference: (p: LayoutPreference) => void;
} {
  const [preference] = useState<LayoutPreference>(readPreference);
  const [mode, setMode] = useState<LayoutMode>(() => resolveMode(readPreference()));

  useEffect(() => {
    const update = () => setMode(resolveMode(readPreference()));
    update();

    const mqWidth = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const mqPointer = window.matchMedia("(pointer: coarse)");
    mqWidth.addEventListener("change", update);
    mqPointer.addEventListener("change", update);
    return () => {
      mqWidth.removeEventListener("change", update);
      mqPointer.removeEventListener("change", update);
    };
  }, [preference]);

  const setPreference = useCallback((p: LayoutPreference) => {
    setLayoutPreference(p);
  }, []);

  return { mode, preference, setPreference };
}
